/**
 * Client HTTP typé de l’API v1 (DECISIONS §4.1, `docs/api-v1.md` §4.5 et §11).
 *
 * Responsabilités, et rien de plus :
 * - préfixer par `env.apiBaseUrl`, injecter `Authorization: Bearer` et `Idempotency-Key` ;
 * - décoder les enveloppes `{ data, meta }` / `{ error, meta }` avec le décodeur de
 *   `@elemora/shared` (une seule vérité de contrat, pas de parsing maison) ;
 * - sur `401`, tenter **un seul** `POST /auth/refresh` puis rejouer la requête une fois ;
 *   un second échec purge la session en mémoire ;
 * - ne jamais laisser fuir une exception réseau brute : tout devient `ErreurApi`.
 *
 * Ce que ce module ne fait **jamais** : calculer un montant, décider d’un gain, écrire le jeton
 * dans un stockage persistant (DECISIONS §8).
 */
import { parseEnvelope, refreshResponseSchema } from '@elemora/shared/api';
import {
  API_PATHS,
  AUTHORIZATION_HEADER,
  BEARER_PREFIX,
  IDEMPOTENCY_HEADER,
} from '@elemora/shared/constants';


import { ErreurApi, versErreurApi } from './erreurs';
import { lireAccessToken, useSessionStore } from './session-store';
import { env } from '../app/env';

import type { z } from 'zod';

export interface OptionsRequete<T> {
  readonly methode: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  /** Chemin relatif à `env.apiBaseUrl`, ex. `/auth/login`. */
  readonly chemin: string;
  /** Schéma zod de la charge utile attendue (déjà livré par `@elemora/shared`). */
  readonly schema: z.ZodType<T>;
  /** Corps JSON sérialisé tel quel. */
  readonly corps?: unknown;
  /** `false` pour les routes publiques et les routes à cookie de refresh. Défaut : `true`. */
  readonly authentifiee?: boolean;
  /** Header `Idempotency-Key` (obligatoire sur les trois routes sensibles, §4.1). */
  readonly cleIdempotence?: string;
  readonly signal?: AbortSignal;
}

type OptionsTransport = Omit<OptionsRequete<unknown>, 'schema'>;

/** Construit les en-têtes d’une requête. */
function construireEntetes(options: OptionsTransport, jeton: string | null): Headers {
  const entetes = new Headers({ Accept: 'application/json' });
  if (options.corps !== undefined) entetes.set('Content-Type', 'application/json');
  if (options.authentifiee !== false && jeton !== null) {
    entetes.set(AUTHORIZATION_HEADER, `${BEARER_PREFIX}${jeton}`);
  }
  if (options.cleIdempotence !== undefined) {
    entetes.set(IDEMPOTENCY_HEADER, options.cleIdempotence);
  }
  return entetes;
}

/** Lit le corps d’une réponse sans jamais lever : un corps illisible vaut `undefined`. */
async function lireCorps(reponse: Response): Promise<unknown> {
  const texte = await reponse.text();
  if (texte.length === 0) return undefined;
  try {
    return JSON.parse(texte) as unknown;
  } catch {
    return undefined;
  }
}

/** Exécute l’appel réseau, en convertissant un échec de transport en `ErreurApi`. */
async function envoyer(
  options: OptionsTransport,
  jeton: string | null,
  signal: AbortSignal | undefined,
): Promise<Response> {
  try {
    return await fetch(`${env.apiBaseUrl}${options.chemin}`, {
      method: options.methode,
      headers: construireEntetes(options, jeton),
      credentials: 'include',
      ...(options.corps === undefined ? {} : { body: JSON.stringify(options.corps) }),
      ...(signal === undefined ? {} : { signal }),
    });
  } catch (erreur) {
    throw new ErreurApi({
      code: 'INTERNAL_ERROR',
      message: erreur instanceof Error ? erreur.message : 'Échec réseau',
      statutHttp: 0,
      reseau: true,
    });
  }
}

/** Rafraîchissement en cours : plusieurs `401` simultanés ne déclenchent qu’un seul refresh. */
let rafraichissementEnCours: Promise<string | null> | null = null;

async function effectuerRafraichissement(): Promise<string | null> {
  try {
    const reponse = await fetch(`${env.apiBaseUrl}${API_PATHS.auth.refresh}`, {
      method: 'POST',
      headers: { Accept: 'application/json' },
      credentials: 'include',
    });
    const resultat = parseEnvelope(await lireCorps(reponse), refreshResponseSchema);
    if (!resultat.ok) {
      useSessionStore.getState().deconnecter();
      return null;
    }
    useSessionStore.getState().definirAccessToken(resultat.data.accessToken);
    return resultat.data.accessToken;
  } catch {
    useSessionStore.getState().deconnecter();
    return null;
  }
}

/**
 * Rafraîchit le jeton d’accès (un seul appel à la fois).
 *
 * Renvoie `null` si le refresh échoue ; la session est alors purgée et la garde d’accès
 * redirige vers `/connexion` (`docs/api-v1.md` §4.5).
 */
export function rafraichirAccessToken(): Promise<string | null> {
  if (rafraichissementEnCours === null) {
    rafraichissementEnCours = effectuerRafraichissement().finally(() => {
      rafraichissementEnCours = null;
    });
  }
  return rafraichissementEnCours;
}

/** Envoie la requête ; sur `401`, rafraîchit **une fois** et rejoue **une fois**. */
async function envoyerAvecReprise(
  options: OptionsTransport,
  signal: AbortSignal | undefined,
): Promise<Response> {
  let reponse = await envoyer(options, lireAccessToken(), signal);
  if (reponse.status === 401 && options.authentifiee !== false) {
    const jeton = await rafraichirAccessToken();
    if (jeton !== null) {
      reponse = await envoyer(options, jeton, signal);
    }
  }
  return reponse;
}

/**
 * Appelle l’API et renvoie la charge utile décodée.
 *
 * Toute réponse hors contrat (HTML d’un proxy en panne, `meta` manquant…) devient une
 * `INTERNAL_ERROR` explicite : jamais un `undefined` silencieux dans un écran.
 */
export async function requete<T>(options: OptionsRequete<T>): Promise<T> {
  const reponse = await envoyerAvecReprise(options, options.signal);
  const resultat = parseEnvelope(await lireCorps(reponse), options.schema);
  if (!resultat.ok) {
    throw new ErreurApi({
      code: resultat.code,
      message: resultat.message,
      statutHttp: reponse.status,
      ...(resultat.details === undefined ? {} : { details: resultat.details }),
      requestId: resultat.meta.requestId,
    });
  }
  return resultat.data;
}

/**
 * Appelle une route sans corps de réponse (`204`), ex. `POST /auth/logout`.
 *
 * Le contrat ne définit pas d’enveloppe pour un `204` : on valide donc seulement le statut HTTP.
 */
export async function requeteSansContenu(options: OptionsTransport): Promise<void> {
  const reponse = await envoyerAvecReprise(options, undefined);
  if (reponse.ok) return;
  const charge = await lireCorps(reponse);
  throw versErreurApi(
    new ErreurApi({
      code: 'INTERNAL_ERROR',
      message: `Réponse ${reponse.status} inattendue`,
      statutHttp: reponse.status,
      ...(charge === undefined ? {} : { details: { corps: charge } }),
    }),
  );
}

/**
 * Génère une clé d’idempotence (UUID v4) pour une action utilisateur.
 *
 * Une clé est générée **une fois par action** puis conservée pendant les tentatives : un nouveau
 * clic produit une nouvelle clé (DECISIONS §4.1, `docs/api-v1.md` §11.2).
 */
export function genererCleIdempotence(): string {
  const cryptoApi = globalThis.crypto;
  if (cryptoApi !== undefined && typeof cryptoApi.randomUUID === 'function') {
    return cryptoApi.randomUUID();
  }
  // Repli pour les navigateurs sans Web Crypto (aucune garantie cryptographique nécessaire :
  // la clé sert uniquement à dédupliquer une action, le serveur reste l’autorité).
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (caractere) => {
    const aleatoire = Math.floor(Math.random() * 16);
    const valeur = caractere === 'x' ? aleatoire : (aleatoire & 0x3) | 0x8;
    return valeur.toString(16);
  });
}
